import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Divider,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  useTheme,
  Container,
  Stack
} from '@mui/material';
import {
  RequestQuote as RequestQuoteIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Create as CreateIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import financialRequestService from '../../../services/financialRequestService';
import MemberSidebar from '../../../components/Layout/MemberSidebar';

const reasonOptions = [
  'Medical Expenses',
  'Educational Support',
  'Housing Assistance',
  'Utility Bills',
  'Transportation Needs',
  'Family Emergency',
  'Funeral Expenses',
  'Job Loss',
  'Food Assistance',
  'Other'
];

const CreateFinancialRequestPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    reason: '',
    otherReason: '',
    amount: '',
    details: ''
  });
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Select Reason', 'Provide Details', 'Submit Request'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    // Validate based on current step
    if (activeStep === 0) {
      if (!formData.reason) {
        newErrors.reason = 'Please select a reason for your request';
      } else if (formData.reason === 'Other' && !formData.otherReason) {
        newErrors.otherReason = 'Please specify the reason for your request';
      }
    } else if (activeStep === 1) {
      if (!formData.details) {
        newErrors.details = 'Please provide details about your request';
      } else if (formData.details.length < 20) {
        newErrors.details = 'Please provide more detailed information (at least 20 characters)';
      }
      
      if (formData.amount && (isNaN(formData.amount) || parseFloat(formData.amount) <= 0)) {
        newErrors.amount = 'Please enter a valid amount greater than 0';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    setError('');

    try {
      // Prepare the request data
      const requestData = {
        reason: formData.reason === 'Other' ? formData.otherReason : formData.reason,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        details: formData.details
      };

      // Call the API to create the request
      await financialRequestService.createRequest(requestData);
      
      // Show success message
      setSuccess(true);
      
      // Redirect to the financial requests list after a delay
      setTimeout(() => {
        navigate('/member/financial-requests');
      }, 2000);
    } catch (err) {
      console.error('Error creating financial request:', err);
      setError(err.message || 'An error occurred while submitting your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  const handleCancel = () => {
    navigate('/member/financial-requests');
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Request Reason
            </Typography>
            <FormControl fullWidth error={!!errors.reason} sx={{ mb: 3 }}>
              <InputLabel id="reason-label">Reason for Request</InputLabel>
              <Select
                labelId="reason-label"
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                label="Reason for Request"
              >
                {reasonOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.reason ? (
                <FormHelperText error>{errors.reason}</FormHelperText>
              ) : (
                <FormHelperText>Select the primary reason for your financial assistance request</FormHelperText>
              )}
            </FormControl>

            {formData.reason === 'Other' && (
              <TextField
                fullWidth
                required
                id="otherReason"
                name="otherReason"
                label="Please specify reason"
                value={formData.otherReason}
                onChange={handleChange}
                error={!!errors.otherReason}
                helperText={errors.otherReason || "Enter the specific reason for your request"}
                sx={{ mb: 3 }}
              />
            )}
            
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'primary.lighter', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center'
                }}
              >
                <InfoIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                Please select a reason that best describes your need for financial assistance. 
                This helps us properly review and process your request.
              </Typography>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Provide Request Details
            </Typography>
            
            <TextField
              fullWidth
              id="amount"
              name="amount"
              label="Amount Needed (optional)"
              type="number"
              value={formData.amount}
              onChange={handleChange}
              error={!!errors.amount}
              helperText={errors.amount || "Leave blank if you're unsure about the exact amount needed"}
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              required
              id="details"
              name="details"
              label="Additional Details"
              multiline
              rows={6}
              value={formData.details}
              onChange={handleChange}
              error={!!errors.details}
              helperText={errors.details || "The more details you provide, the better we can understand your needs"}
              placeholder="Please provide more information about your situation and how this assistance will help you"
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'info.lighter', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center'
                }}
              >
                <InfoIcon color="info" fontSize="small" sx={{ mr: 1 }} />
                Please provide specific details about your circumstances and how the requested assistance 
                will help address your needs. This information helps us make informed decisions when 
                reviewing your request.
              </Typography>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Request Reason
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.reason === 'Other' ? formData.otherReason : formData.reason}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Requested Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.amount ? `$${parseFloat(formData.amount).toFixed(2)}` : 'Not specified'}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Details
                  </Typography>
                  <Typography variant="body1">
                    {formData.details}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            <Box sx={{ 
              p: 2, 
              bgcolor: 'warning.lighter', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'warning.light',
              mb: 3
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start'
                }}
              >
                <InfoIcon color="warning" fontSize="small" sx={{ mr: 1, mt: 0.25 }} />
                Please review your request details carefully before submitting. Once submitted, 
                this request will be reviewed by our team. You can check the status of your request 
                on the Financial Assistance Requests page.
              </Typography>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
 
      <React.Fragment>
        <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton 
                color="inherit" 
                onClick={handleCancel}
                sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <RequestQuoteIcon sx={{ mr: 1.5 }} />
                  Request Financial Assistance
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.8, mt: 0.5 }}>
                  Fill out this form to submit a request for financial support
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Card sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            mb: 3
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel
                sx={{ mb: 5 }}
              >
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          ...(index === 0 && { 
                            '& .MuiStepIcon-root': { color: 'primary.main' } 
                          }),
                          ...(index === 1 && { 
                            '& .MuiStepIcon-root': { color: 'secondary.main' } 
                          }),
                          ...(index === 2 && { 
                            '& .MuiStepIcon-root': { color: 'success.main' } 
                          }),
                        }
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              <Box component="form" onSubmit={handleSubmit}>
                {getStepContent(activeStep)}
                
                {error && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mt: 4 
                }}>
                  <Button
                    variant="outlined"
                    onClick={activeStep === 0 ? handleCancel : handleBack}
                    disabled={loading}
                    sx={{ borderRadius: '8px' }}
                    startIcon={activeStep === 0 ? <ArrowBackIcon /> : undefined}
                  >
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                  </Button>
                  
                  <Box>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                        sx={{ 
                          borderRadius: '8px',
                          px: 3,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                      >
                        {loading ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ borderRadius: '8px' }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            p: 3, 
            borderRadius: 3, 
            bgcolor: 'background.paperLight',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <React.Fragment>
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    About Financial Assistance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Our financial assistance program is designed to help members with unexpected financial 
                    needs. All requests are evaluated based on demonstrated need, available funds, and our 
                    established guidelines.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The review process typically takes 3-5 business days. You can check the status of your 
                    request at any time from the Financial Assistance Requests page.
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
          </Card>
        </Box>
        
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="success" 
            variant="filled"
            sx={{ width: '100%' }}
            icon={<CheckCircleIcon />}
          >
            Your financial assistance request has been submitted successfully!
          </Alert>
        </Snackbar>
      </React.Fragment>
  
  );
};

export default CreateFinancialRequestPage;